import { PROPOSAL_ACCENT, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import {
  resolveProposalSubtitle,
  resolveProposalTitle,
  type DtoolsProposalPresentation,
} from "@shared/dtoolsProposalPresentation";
import type { ProposalPresentationHeaderInput } from "@shared/dtoolsProposalPresentation";

type Props = {
  header: ProposalPresentationHeaderInput;
  presentation: DtoolsProposalPresentation;
  /** When true, only hero image + introduction (cover already shown above). */
  contentOnly?: boolean;
};

export default function DtoolsProposalPresentationHeader({ header, presentation, contentOnly = false }: Props) {
  const title = resolveProposalTitle(header, presentation);
  const subtitle = resolveProposalSubtitle(header, presentation);
  const intro = presentation.customerIntroduction?.trim();
  const heroUrl = presentation.heroImageUrl?.trim();

  if (contentOnly && !heroUrl && !intro) return null;

  return (
    <header className="space-y-6" style={{ fontFamily: PROPOSAL_COPY_FONT }}>
      {!contentOnly ? (
        <div className="space-y-2 border-b border-[#C8A96A]/40 pb-6">
          <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: PROPOSAL_ACCENT }}>
            Proposal
          </p>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: PROPOSAL_TEXT }}>
            {title}
          </h1>
          {subtitle ? (
            <p className="text-base text-[#1A2744]/75 sm:text-lg">{subtitle}</p>
          ) : null}
        </div>
      ) : null}

      {heroUrl ? (
        <div className="overflow-hidden rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7]">
          <img src={heroUrl} alt={title} className="max-h-[420px] w-full object-cover" />
        </div>
      ) : null}

      {intro ? (
        <div className="rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7]/80 p-5 sm:p-6">
          <p className="text-xs font-medium uppercase tracking-[0.16em]" style={{ color: PROPOSAL_ACCENT }}>
            Introduction
          </p>
          <p className="mt-3 text-sm leading-7 whitespace-pre-wrap text-[#1A2744]/90">{intro}</p>
        </div>
      ) : null}
    </header>
  );
}
